import { PRIORITY, STATUS } from '@/entities/alert/model/types';
import type { AxiosRequestConfig } from 'axios';
import z from 'zod';
import { api } from '../../axios.instance';
import {
  PagePaginationResponseDtoSchema,
  responseContract,
  type PagePaginationRequestDto,
} from '../../lib';

const GetAlertsResponseDtoSchema = PagePaginationResponseDtoSchema(
  z.object({
    createdAt: z.string(),
    daysOpen: z.number(),
    facility: z.object({
      id: z.number(),
      name: z.string(),
    }),
    id: z.number(),
    incidentDateTime: z.string(),
    daysRemaining: z.number().nullable(),
    dueAt: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    incidentType: z.string(),
    isOverdue: z.boolean(),
    status: z.enum(STATUS),
    priority: z.enum(PRIORITY).nullable(),
    resident: z
      .object({
        id: z.number(),
        fullName: z.string(),
        levelOfCare: z.string().nullish(),
      })
      .nullable(),
  })
);

const _GetAlertsRequestDtoOptionsSchema = z.object({
  facilityIds: z.array(z.number()).optional(),
  residentId: z.number().optional(),
  statuses: z.array(z.enum(STATUS)),
  priorities: z.array(z.enum(PRIORITY)),
  alertConfigNames: z.array(z.string()),
  search: z.string().optional(),
});

type GetAlertsRequestDtoOptions = z.infer<
  typeof _GetAlertsRequestDtoOptionsSchema
>;

export type GetAlertsRequestDto = PagePaginationRequestDto<
  GetAlertsRequestDtoOptions,
  | 'incidentDateTime'
  | 'createdAt'
  | 'dueAt'
  | 'facilityName'
  | 'residentName'
  | 'priority'
  | 'status'
>;
export type GetAlertsResponseDto = z.infer<typeof GetAlertsResponseDtoSchema>;

export const getAlerts = (
  params: GetAlertsRequestDto,
  config?: AxiosRequestConfig<GetAlertsRequestDto>
) => {
  const transformedParams: GetAlertsRequestDto = {
    ...params,
    search: params.search?.trim() ? params.search.trim() : undefined,
  };

  return api
    .get('/alerts', {
      params: transformedParams,
      ...config,
    })
    .then(responseContract(GetAlertsResponseDtoSchema));
};
